'use client';

import { useState, useEffect, useRef } from 'react';
import { setCookie, hasCookie } from 'cookies-next';
import { motion, AnimatePresence } from 'motion/react';
import Link from 'next/link';

import Portal from 'app/components/common/Portal';
import ImageBg from 'app/components/common/ImageBg';
import LockBodyScroll from 'app/components/common/LockBodyScroll';
import { IconCloseModal } from 'app/components/sprites';
import usePreloaderMainStore from 'app/components/common/PreloaderMain/store';
import {
  defaultProcessText,
  defaultProcessTextWithoutLinks
} from 'app/utility/textProcessing';
import { IImage } from 'app/interfaces/common';

// Через сколько после скрытия прелоадера показывать модалку (мс).
const SHOW_DELAY = 3000;
// Сколько «помнить» закрытие модалки (сек) — 1 день.
const TIME_TO_EXPIRES = 24 * 60 * 60;
const COOKIE_NAME = 'promo_banner_important_closed';

interface PromoBannerImportantProps {
  content: string | null;
  image?: IImage | null;
  url?: string | null;
}

/** Оборачивает содержимое в ссылку, если задан url. */
function BannerLink({
  url,
  children
}: { url?: string | null } & React.PropsWithChildren) {
  if (!url) return <>{children}</>;

  return (
    <Link href={url} target="_blank" className="block">
      {children}
    </Link>
  );
}

/**
 * «Важный» промо-баннер — модальное окно поверх страницы с картинкой и
 * текстом. Открывается через SHOW_DELAY после того, как спрятался прелоадер.
 */
export default function PromoBannerImportant({
  content,
  image,
  url
}: PromoBannerImportantProps) {
  const [isShown, setIsShown] = useState(false);
  const { isHidden: isPreloaderHidden } = usePreloaderMainStore();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Ждём, пока спрячется прелоадер; не показываем, если модалку уже закрывали.
    if (!isPreloaderHidden || hasCookie(COOKIE_NAME)) return;

    timerRef.current = setTimeout(() => setIsShown(true), SHOW_DELAY);

    return () => {
      clearTimeout(timerRef.current || 0);
    };
  }, [isPreloaderHidden]);

  const handleClose = () => {
    setCookie(COOKIE_NAME, 'true', {
      path: '/',
      maxAge: TIME_TO_EXPIRES
    });
    setIsShown(false);
  };

  if (!content) return null;

  return (
    <Portal>
      <AnimatePresence>
        {isShown && (
          <motion.div
            data-lenis-prevent
            className="fixed inset-0 z-50 overflow-auto no-scrollbar"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <LockBodyScroll />

            {/* Оверлей: клик по нему закрывает модалку */}
            <div
              className="fixed inset-0 bg-graphite-dark/50 backdrop-blur-[50px]"
              onClick={handleClose}
            />

            <div className="relative min-h-full flex items-center justify-center wrapper-main py-22">
              <motion.div
                className="w-full max-w-[473px] bg-white rounded-xl p-3"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                exit={{ y: 20 }}
                transition={{ duration: 0.4 }}
              >
                <BannerLink url={url}>
                  {!!image?.url && (
                    <div className="relative pb-[62%] md:pb-[43%] rounded overflow-hidden">
                      <ImageBg
                        src={image.url}
                        alt={image.alt || ''}
                        fill
                        sizes="(max-width: 768px) 100vw, 473px"
                        placeholderDataURL={image.placeholder_data_url}
                        skeletonTheme="gray"
                        className="object-cover"
                      />
                    </div>
                  )}

                  <div
                    // По макету текст занимает не всю ширину карточки (315px + отступы).
                    className="md:max-w-[355px] px-5 pt-6 pb-5 text-brown text-p2 [&_p+p]:mt-5 [&_a]:font-book"
                    dangerouslySetInnerHTML={{
                      // Когда задан url, вся карточка — это <Link> (<a>), поэтому
                      // ссылки внутри content вырезаем: вложенный <a> в <a> ломает
                      // гидратацию в React 19. Без url ссылки контента сохраняем.
                      __html: url
                        ? defaultProcessTextWithoutLinks(content)
                        : defaultProcessText(content)
                    }}
                  ></div>
                </BannerLink>
              </motion.div>
            </div>

            <button
              type="button"
              onClick={handleClose}
              aria-label="Закрыть"
              className="button w-12 h-12 xl:w-15 xl:h-15 rounded bg-graphite fixed top-5 right-3 md:right-6 group"
            >
              <IconCloseModal className="w-[14px] h-4 fill-white group-hover:fill-brown transition-colors duration-300" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
}
